import React, { useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import { Layout, Seo } from '@layout';
import { WonderfulWebsiteSectionV2 } from '@sections/save-the-date';
import { AbcSection } from '@sections/shared/AbcSection';
import { FAQSection } from '@sections/shared/FAQSection';
import { HighQualityPrintsSection as HighQualityPrintsSectionV2 } from '@sections/shared/HighQualityPrintsSection';
import { HowItWorksSection as HowItWorksSectionV2 } from '@sections/shared/HowItWorksSection';
import { saveTheDateDesignsCanonicalUrl } from '@utils/gallery.util';
import { createFilterGroupsProcessor, useProcessPrintCards } from '@utils/joyPrint.utils';
import { saveTheDatePhotoECards } from '@components/GalleryComponents/GalleryComponents.constants';
import { GalleryPageContent } from '@components/GalleryComponents/GalleryPageContent/GalleryPageContent';
import { HeroSectionWithProps } from '@sections/shared/HeroSectionWithProps';
import { ValuePropsArg } from '@components/ValuePropsSection';
import { useTranslation } from 'react-i18next';

export const query = graphql`
  {
    bliss {
      stationeryTemplatesContainer(filter: { categories: saveTheDate }) {
        pagedResults {
          edges {
            node {
              themeId
              thumbnailUrl
              approved
              enabled
              category
            }
          }
        }
      }
      themeVariants(variantTypes: [savethedate]) {
        variationType
        imageUrl
        printUrl
        vendor
        tags
        colors
        font
        variantColor
        variantName
        variantThemeId
        theme {
          id
          name
          themeId
          marketingData {
            variationType
            imageUrl
            printUrl
            vendor
            tags
            colors
            font
            variantColor
            variantName
            variantThemeId
            theme {
              name
              themeId
            }
          }
        }
      }
    }
  }
`;

const CATEGORIES_IMAGES_DATA = {
  bold: 'save-the-date-gallery/styles/bold.png',
  classic: 'save-the-date-gallery/styles/classic.png',
  cultural: 'save-the-date-gallery/styles/cultural.png',
  destination: 'save-the-date-gallery/styles/destination.png',
  'floral-and-botanical': 'save-the-date-gallery/styles/floral_botanical-v2.png',
  metallic: 'save-the-date-gallery/styles/metallic.png',
  modern: 'save-the-date-gallery/styles/modern-v2.png',
  photo: 'save-the-date-gallery/styles/photo.png',
  rustic: 'save-the-date-gallery/styles/rustic.png',
  seasonal: 'save-the-date-gallery/styles/seasonal.png',
  textured: 'save-the-date-gallery/styles/textured.png',
};

const page: MarcomPageName = 'save-the-date';

const SaveTheDate = ({ data }: { data: BlissGalleryECardsThemesData }) => {
  const { t } = useTranslation();
  const seo = t('seo', { ns: 'saveTheDate' });
  const faq = t('faq', { ns: 'saveTheDate' });
  const highQualityPrintsSectionV2 = t('highQualityPrintsSectionV2', { ns: 'saveTheDate' });
  const howItWorksSectionV2 = t('howItWorksSectionV2', { ns: 'saveTheDate' });
  const hero = t('hero', { ns: 'saveTheDate' });
  const productTypes = t('productTypes', { ns: 'galleryPage' });

  const { cards, setShowDesignsWithMatchingPrints } = useProcessPrintCards(data, 'savethedate', saveTheDatePhotoECards);

  const [chromeless, setChromeless] = useState(false);

  const processFilterGroups = useMemo(() => {
    return createFilterGroupsProcessor(productTypes.saveTheDate as GalleryProductType, data.bliss);
  }, [data, productTypes]);

  const valueProps: ValuePropsArg[] = useMemo(
    () =>
      t('saveTheDate:valueProps').map((valueProp) => ({
        ...valueProp,
        icon: valueProp.icon as ValuePropsArg['icon'],
      })),
    [t]
  );

  const valuePropsMobile: ValuePropsArg[] = useMemo(
    () =>
      t('saveTheDate:valuePropsMobile').map((valueProp) => ({
        ...valueProp,
        icon: valueProp.icon as ValuePropsArg['icon'],
      })),
    [t]
  );

  return (
    <Layout
      isTransparentHeader={true}
      transparentJoyLogoColor="mono7"
      transparentHeaderButtonVariant="defaultPrimeWeddingWebsites"
      hideValueProps
      page={page}
      chromeless={chromeless}
    >
      <Seo title={seo.title} description={seo.content} canonicalURL={saveTheDateDesignsCanonicalUrl} />
      <HeroSectionWithProps
        page={page}
        sectionId="Save_The_Date_Hero"
        ctaButtonId="Save_The_Date_Hero_CTA"
        heroData={hero}
        valueProps={valueProps}
        valuePropsMobile={valuePropsMobile}
        valuePropsElementId="save-the-date-props"
      />
      <GalleryPageContent
        page={page}
        productType="Save the Date"
        cards={cards}
        hasPhotoECards
        processFilterGroups={processFilterGroups}
        showModal={(isModalOpened) => {
          setChromeless(isModalOpened);
        }}
        onShowDesignsWithMatchingPrintsToggle={setShowDesignsWithMatchingPrints}
        displayHeroSection={false}
        initialProductCount={12}
        displayFilterTab={false}
        defaultProductType={productTypes.prints as GalleryProductType}
        sectionId="gallery-grid-wrapper"
        displayCategoryImages
        categoryImagesData={CATEGORIES_IMAGES_DATA}
        hideCategoryImagesScrollbar
      />
      <HighQualityPrintsSectionV2 highQualityPrintsSectionData={highQualityPrintsSectionV2} />
      <HowItWorksSectionV2 howItWorksSectionData={howItWorksSectionV2} />
      <WonderfulWebsiteSectionV2 />
      <FAQSection
        id="save-the-date-faq"
        title={faq.title}
        description={faq.description}
        questionNodes={t('questions.saveTheDateDesignV2', { ns: 'faq' })}
      />
      <AbcSection page={page} abcSectionId="Save_the_Date_Design_ABC_CTA" />
    </Layout>
  );
};

export default SaveTheDate;
